/**
 * src/api/cookies.js
 *
 * Purpose: cookie + session-storage helpers — the re-authored equivalent of the
 * deployed pro-ui module "0ce8" (export "f" = the unified read/set/delete cookie
 * helper `B(name[, value[, opts]])`; export "p"/"c" = master-session-key
 * localStorage read/remove) and the `session_key` reader used by module "2594".
 *
 * Contract that is VERBATIM (survives minification, re-greppable):
 *   - cookie name `session_key` (30-day, path "/"), sent as body field `hash`
 *   - cookie name `check_audit` (2h login-audit throttle)
 *   - Config.cookies.service = `dService`
 *   - localStorage keys `session_key`, `master-session-key`, `master-locale`,
 *     `service_session`
 * The read/set/delete mechanics are re-authored.
 *
 * `cookie(name[, value[, opts]])` semantics (VERBATIM from "0ce8" export "f"):
 *   - value omitted            -> READ  (returns the string value or undefined)
 *   - value === false          -> DELETE (expires Thu, 01 Jan 1970)
 *   - otherwise                -> SET   (Path defaults "/", numeric expires = seconds)
 *
 * Reconstructed from lacak.io deployed behavior; app-specific contract verbatim,
 * internal impl re-authored.
 */

// --- VERBATIM key names ---
export const SESSION_KEY_COOKIE = 'session_key';
export const CHECK_AUDIT_COOKIE = 'check_audit';
export const SERVICE_COOKIE = 'dService';
export const MASTER_SESSION_KEY = 'master-session-key';
export const MASTER_LOCALE_KEY = 'master-locale';
export const SERVICE_SESSION_KEY = 'service_session';

// 30 days, in milliseconds — the session_key cookie lifetime.
const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

function hasDocument() {
  return typeof document !== 'undefined' && typeof document.cookie === 'string';
}

function readRawCookie(name) {
  if (!hasDocument()) return undefined;
  const target = encodeURIComponent(name);
  const pairs = document.cookie ? document.cookie.split('; ') : [];
  for (let i = 0; i < pairs.length; i++) {
    const eq = pairs[i].indexOf('=');
    const k = eq < 0 ? pairs[i] : pairs[i].slice(0, eq);
    if (k === target || k === name) {
      const raw = eq < 0 ? '' : pairs[i].slice(eq + 1);
      try {
        return decodeURIComponent(raw);
      } catch (e) {
        return raw;
      }
    }
  }
  return undefined;
}

function writeCookie(name, value, opts) {
  if (!hasDocument()) return;
  const o = opts || {};
  let str = encodeURIComponent(name) + '=' + encodeURIComponent(value);
  const path = o.path || '/';
  str += '; path=' + path;

  if (o.expires != null) {
    let expires = o.expires;
    if (typeof expires === 'number') {
      // numeric expires = seconds (VERBATIM "0ce8" behaviour)
      const d = new Date();
      d.setTime(d.getTime() + expires * 1000);
      expires = d;
    }
    if (expires instanceof Date) {
      str += '; expires=' + expires.toUTCString();
    } else {
      str += '; expires=' + String(expires);
    }
  }
  if (o.domain) str += '; domain=' + o.domain;
  if (o.secure) str += '; secure';
  if (o.sameSite) str += '; samesite=' + o.sameSite;

  document.cookie = str;
}

function deleteCookie(name, opts) {
  if (!hasDocument()) return;
  const path = (opts && opts.path) || '/';
  document.cookie =
    encodeURIComponent(name) +
    '=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=' +
    path;
}

/**
 * The unified helper. Mirrors "0ce8" export "f".
 */
export function cookie(name, value, opts) {
  if (value === undefined) return readRawCookie(name);
  if (value === false) {
    deleteCookie(name, opts);
    return undefined;
  }
  writeCookie(name, value, opts);
  return value;
}

// --- explicit get/set/delete aliases ---
export function getCookie(name) {
  return readRawCookie(name);
}
export function setCookie(name, value, opts) {
  writeCookie(name, value, opts);
}
export function removeCookie(name, opts) {
  deleteCookie(name, opts);
}

// --- session_key (the authoritative session store) ---
export function getSessionKey() {
  return readRawCookie(SESSION_KEY_COOKIE);
}

/**
 * Set the session_key cookie for 30 days at path "/". Callers that need the
 * exact dayjs-based expiry used by the `user/setSession` action may pass their
 * own Date via `expires`; the default is +30 days computed natively.
 */
export function setSessionKey(hash, expires) {
  writeCookie(SESSION_KEY_COOKIE, hash, {
    path: '/',
    expires: expires || new Date(Date.now() + THIRTY_DAYS_MS)
  });
}

export function clearSessionKey() {
  deleteCookie(SESSION_KEY_COOKIE);
}

// --- check_audit (2h login-audit throttle) ---
export function getCheckAudit() {
  return readRawCookie(CHECK_AUDIT_COOKIE);
}
export function setCheckAudit(value, expires) {
  // default 2 hours
  writeCookie(CHECK_AUDIT_COOKIE, value, {
    path: '/',
    expires: expires || new Date(Date.now() + 2 * 60 * 60 * 1000)
  });
}
export function clearCheckAudit() {
  deleteCookie(CHECK_AUDIT_COOKIE);
}

// --- dService (service/dealer marker) ---
export function getServiceCookie() {
  return readRawCookie(SERVICE_COOKIE);
}
export function setServiceCookie(value, opts) {
  writeCookie(SERVICE_COOKIE, value, opts || { path: '/' });
}

// --- master-session-key (parked master session during impersonation) ---
function hasLocalStorage() {
  try {
    return typeof window !== 'undefined' && !!window.localStorage;
  } catch (e) {
    return false;
  }
}

export function getMasterSessionKey() {
  return hasLocalStorage() ? window.localStorage.getItem(MASTER_SESSION_KEY) : null;
}
export function setMasterSessionKey(hash) {
  if (hasLocalStorage()) window.localStorage.setItem(MASTER_SESSION_KEY, hash);
}
export function clearMasterSession() {
  if (hasLocalStorage()) window.localStorage.removeItem(MASTER_SESSION_KEY);
}

export function getMasterLocale() {
  return hasLocalStorage() ? window.localStorage.getItem(MASTER_LOCALE_KEY) : null;
}
export function setMasterLocale(locale) {
  if (hasLocalStorage()) window.localStorage.setItem(MASTER_LOCALE_KEY, locale);
}

// alternate presence flag the router guard also accepts
export function getSessionKeyLS() {
  return hasLocalStorage() ? window.localStorage.getItem(SESSION_KEY_COOKIE) : null;
}

export function getServiceSession() {
  return hasLocalStorage() ? window.localStorage.getItem(SERVICE_SESSION_KEY) : null;
}

export default cookie;
